import type { Cotizacion, Pieza, Sitio } from "./types";
import { FORMATO_RESUMEN } from "./model";
// Tasas del código anterior; no equivalen a aprobación de Producción DV.
// Solo se aplican a piezas armadas por su lista de materiales.
export const FORMULA = Object.freeze({ imprevistos: 0.05, manoDeObra: 0.3 });
// m² de UNA pieza: los que escribió el vendedor directo, o ancho × alto.
export function areaM2(p: Pieza): number {
  return p.areaM2 != null ? p.areaM2 : (p.anchoCm * p.altoCm) / 10000;
}
export function costoMateriales(p: Pieza): number {
  return (
    (p.materiales ?? []).reduce(
      (s, m) => s + m.cantidad * m.precioUnitario * (m.porArea ? areaM2(p) : 1),
      0,
    ) * p.cantidad
  );
}
// Precio de venta manual de la pieza: el total escrito, o el precio por m²
// × m² × cantidad (redondeado a centavos, como lo recibe n8n).
export function precioAjustadoDe(p: Pieza): number | null {
  if (p.precioAjustadoM2 != null && ["m2", "cm2"].includes(p.unidad))
    return Math.round(p.precioAjustadoM2 * areaM2(p) * p.cantidad * 100) / 100;
  return p.precioAjustado;
}
export function subtotalPieza(p: Pieza): number {
  if (p.personalizado) return (p.precioUnitario ?? 0) * areaM2(p) * p.cantidad;
  if (p.precioAjustado != null) return precioAjustadoDe(p) ?? 0;
  // El precio de contrato se cobra en la unidad de la variante (ej. Austro
  // $/m²), pero sigue fuera de la fórmula: ver calcularPieza.
  if (p.precioContratado != null) return precioPorUnidad(p.precioContratado, p);
  if (p.materiales?.length) return costoMateriales(p);
  return precioPorUnidad(p.precioUnitario ?? 0, p);
}
function precioPorUnidad(precio: number, p: Pieza): number {
  switch (p.unidad) {
    case "unidad":
      return precio * p.cantidad;
    case "ml":
      return ((precio * p.anchoCm) / 100) * p.cantidad;
    case "cm_lineal":
      return precio * p.anchoCm * p.cantidad;
    case "cm2":
      return precio * areaM2(p) * 10000 * p.cantidad;
    default:
      return precio * areaM2(p) * p.cantidad;
  }
}
export function calcularPieza(p: Pieza, margenSitio: number) {
  const base = subtotalPieza(p);
  // Precio final: ajuste manual, contrato o catálogo del cliente. No recibe
  // nada encima.
  const bypass =
    p.precioAjustado != null ||
    p.precioContratado != null ||
    p.catalogoEspecial != null;
  // Imprevistos y mano de obra solo en piezas armadas por su lista de
  // materiales (costos de materia prima). El catálogo general y las piezas
  // personalizadas ya son precio de venta (Diez Visual, 21-oct-2026).
  const porMateriales = !bypass && !p.personalizado && !!p.materiales?.length;
  const materiales = porMateriales ? base : 0;
  const imprevistos = materiales * FORMULA.imprevistos;
  const manoDeObra = (materiales + imprevistos) * FORMULA.manoDeObra;
  const costo = porMateriales ? materiales + imprevistos + manoDeObra : base;
  const margenPct = p.margenOverride ?? margenSitio;
  const margen = bypass ? 0 : costo * margenPct;
  const bruto = costo + margen;
  const descuento = bruto * p.descuentoPct;
  return {
    id: p.id,
    base,
    bypass,
    porMateriales,
    materiales,
    imprevistos,
    manoDeObra,
    costo,
    margen,
    margenPct,
    bruto,
    descuento,
    total: bruto - descuento,
  };
}
export function calcularSitio(s: Sitio, margenGlobal: number) {
  const margen = s.margenOverride ?? margenGlobal;
  const piezas = s.productos.flatMap((pr) =>
    pr.piezas.map((p) => calcularPieza(p, margen)),
  );
  const l = s.logistica;
  const logistica =
    l.totalManual ?? (l.incluida ? l.visitas * l.costoPorVisita : 0);
  const addons = s.addons.reduce((sum, a) => sum + a.costo, 0);
  const total =
    piezas.reduce((sum, p) => sum + p.total, 0) + logistica + addons;
  return { id: s.id, piezas, logistica, addons, total };
}
export function calcularCotizacion(c: Cotizacion) {
  const sitios = c.sitios.map((s) =>
    calcularSitio(s, c.margenActivo ? c.margen : 0),
  );
  const piezas = sitios.flatMap((s) => s.piezas);
  const bruto = sitios.reduce((sum, s) => sum + s.total, 0);
  const descuento = bruto * c.descuento;
  const subtotal = bruto - descuento;
  const iva = c.ivaIncluido ? (subtotal * c.ivaPct) / 100 : 0;
  const sumar = (
    key:
      | "materiales"
      | "imprevistos"
      | "manoDeObra"
      | "costo"
      | "margen"
      | "descuento",
  ) => piezas.reduce((s, p) => s + p[key], 0);
  return {
    sitios,
    piezas,
    materiales: sumar("materiales"),
    imprevistos: sumar("imprevistos"),
    manoDeObra: sumar("manoDeObra"),
    // Precio de las piezas antes de margen y descuentos: lo que ve el
    // vendedor en el resumen (sin desglose de imprevistos ni mano de obra).
    productos: sumar("costo"),
    margen: sumar("margen"),
    descuentosPieza: sumar("descuento"),
    logistica: sitios.reduce((sum, s) => sum + s.logistica + s.addons, 0),
    bypass: piezas.filter((p) => p.bypass).reduce((sum, p) => sum + p.base, 0),
    bruto,
    descuento,
    subtotal,
    iva,
    total: subtotal + iva,
  };
}
// Lo que le falta a UNA pieza para poder cotizarse, en frases cortas sin el
// nombre delante: lo muestra la propia tarjeta (Piezas.tsx) y lo usa
// validarCotizacion() con el nombre antepuesto. Una sola fuente, para que la
// tarjeta en rojo, el bloqueo al pasar al paso 3 y el error al generar el PDF
// digan siempre lo mismo.
// Capacitación 03-oct-2026: el aviso no decía que una pieza vacía que sobra
// se puede borrar, y los comerciales no sabían qué hacer con ella.
export const SIN_SELECCIONAR =
  "elígela del catálogo, créala o, si sobra, bórrala con la papelera.";
export function erroresPieza(p: Pieza): string[] {
  const errores: string[] = [];
  if (!p.variante) errores.push(SIN_SELECCIONAR);
  if (
    p.precioUnitario == null &&
    p.precioAjustado == null &&
    p.precioContratado == null &&
    !p.materiales?.length
  )
    errores.push("necesita cotización manual; especifica un precio.");
  // Las piezas por unidad pueden llevar medidas, pero solo informativas.
  if (p.precioAjustado == null && p.unidad !== "unidad") {
    const faltan = ["m2", "cm2"].includes(p.unidad)
      ? p.areaM2 != null
        ? p.areaM2 <= 0
        : p.anchoCm <= 0 || p.altoCm <= 0
      : p.anchoCm <= 0;
    if (faltan) errores.push("completa las medidas.");
  }
  if (!Number.isInteger(p.cantidad) || p.cantidad < 1)
    errores.push("la cantidad debe ser un entero positivo.");
  if (
    [
      p.anchoCm,
      p.altoCm,
      p.profundidadCm,
      p.areaM2 ?? 0,
      p.precioUnitario ?? 0,
      p.precioAjustado ?? 0,
      p.precioContratado ?? 0,
      p.descuentoPct,
      p.margenOverride ?? 0,
      ...(p.materiales ?? []).flatMap((m) => [m.cantidad, m.precioUnitario]),
    ].some((n) => !Number.isFinite(n) || n < 0) ||
    p.descuentoPct > 1
  )
    errores.push("revisa los valores numéricos.");
  if (!["m2", "cm2", "ml", "cm_lineal", "unidad"].includes(p.unidad))
    errores.push("unidad no soportada; consulta a Producción DV.");
  return errores;
}
// Lo que se edita en el paso 2 (piezas, logística y add-ons de cada sitio).
// Es lo único que bloquea pasar al paso 3: lo que se corrige EN el paso 3
// (formato de entrega, margen, descuento, IVA) no puede impedir llegar a él.
// Mismos mensajes y orden que validarCotizacion(), que la incluye.
export function erroresDelPaso2(c: Cotizacion): string[] {
  const errores: string[] = [];
  for (const [i, s] of c.sitios.entries()) {
    const l = s.logistica;
    if (
      [
        s.margenOverride ?? 0,
        l.totalManual ?? 0,
        l.costoPorVisita,
        l.visitas,
        ...s.addons.map((a) => a.costo),
      ].some((n) => !Number.isFinite(n) || n < 0) ||
      (l.incluida && (!Number.isInteger(l.visitas) || l.visitas < 1))
    )
      errores.push(`Sitio ${i + 1}: revisa margen, logística y add-ons.`);
    for (const pr of s.productos)
      for (const p of pr.piezas)
        for (const e of erroresPieza(p))
          errores.push(
            e === SIN_SELECCIONAR
              ? "Hay piezas vacías: elígelas del catálogo, créalas o bórralas con la papelera si sobran."
              : `${p.variante || "Pieza sin seleccionar"}: ${e}`,
          );
  }
  return [...new Set(errores)];
}
// Se corrige en el paso 3, así que no bloquea el paso de la 2 a la 3.
export const ERROR_CONDICIONES = "Revisa el margen, descuento general e IVA.";
export function validarCotizacion(c: Cotizacion): string[] {
  const errores: string[] = [];
  if (!c.cliente.nombre.trim()) errores.push("Completa el nombre del cliente.");
  if (!c.sitios.length) errores.push("Agrega al menos un sitio.");
  if (
    [c.margen, c.descuento, c.ivaPct].some(
      (n) => !Number.isFinite(n) || n < 0,
    ) ||
    c.descuento > 1 ||
    c.ivaPct > 100
  )
    errores.push(ERROR_CONDICIONES);
  errores.push(...erroresDelPaso2(c));
  // En el resumen por tienda y zona una tienda sin piezas es válida: sale en
  // $0 (tienda sin intervención en la campaña, ver sitiosEnCero). Va solo en
  // la validación completa: el formato se elige en el paso 3.
  for (const [i, s] of c.sitios.entries())
    if (
      c.formatoPdf !== FORMATO_RESUMEN &&
      !s.productos.some((pr) => pr.piezas.length)
    )
      errores.push(`Sitio ${i + 1}: agrega una pieza.`);
  if (!Number.isFinite(calcularCotizacion(c).total))
    errores.push("El total excede el rango permitido. Revisa los valores.");
  return [...new Set(errores)];
}
